import React from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  usePagination,
} from "react-table";
import image from "../../../src/assets/svg/artboard.svg";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { PageButton } from "./Button";
import { classNames } from "./utils";
import "./table.css";
import Spinner from "../layout/loader/Spinner";

export function StatusPill({ value }) {
  const status = value ? value.toLowerCase() : "unknown";

  return (
    <div
      className={classNames(
        "px-3 h-[32px]  flex items-center justify-center capitalize leading-wide font-bold text-xs rounded-md shadow-sm",
        status.startsWith("applied") ? "bg-[#DEE7FC] text-[#2563EB]" : null,
        status.startsWith("rejected ") ? "bg-[#FFE1E2] text-[#EF4444]" : null,
        status.startsWith("joined") ? "bg-[#E9FAEB] text-[#0DA06A]" : null,
        status.startsWith("hired") ? "bg-[#FFFDCB] text-[#EFE844]" : null,
        status.startsWith("offered") ? "bg-[#FFE4D7] text-[#FF9A6C]" : null,
        status.startsWith("manager") ? "bg-[#C4C7C9] text-[#181818]" : null,
        status.startsWith("wait") ? "bg-[#FDE7BC] text-[#FFB932]" : null,
        status.startsWith("getwork") ? "bg-[#C3D5E7] text-[#00203F]" : null,
        status.startsWith("pending") ? "bg-[#FDE7BC] text-[#FFB932]" : null
      )}
    >
      {status}
    </div>
  );
}

export function LinkCell({ value, column, row }) {
  return (
    <div className="flex items-center">
      <div className="text-center m-auto">
        <a
          href={row.original[column.linkAccessor]}
          className="text-[#2563EB] underline decoration-1"
        >
          <div className="text-sm font-medium ">{value}</div>
        </a>
      </div>
    </div>
  );
}
// export function Tooltip({ value, column, row }) {
//   return (
//     <div className="flex items-center">
//       <div className="text-center m-auto">
//         <a
//           href={row.original[column.linkAccessor]}
//           className="text-[#2563EB] underline decoration-1"
//         >
//           <div className="text-sm font-medium ">{value}</div>
//         </a>
//       </div>
//     </div>
//   );
// }
export function RupeeCell({ value, column, row }) {
  return (
    <div className="flex items-center">
      <div className="text-center m-auto">
        <p className="text-sm text-[#0DA06A] font-medium text-center">
          {value}
        </p>
      </div>
    </div>
  );
}

export function RedCell({ value }) {
  return (
    <div className="  flex items-center justify-center ">
      <div className="text-center m-auto">
        <div className="text-sm font-medium text-[#EF4444] ">
          {value.slice(0, 60)}
        </div>
      </div>
    </div>
  );
}

function JobTableScreen({
  columns,
  data,
  paginationData,
  setPaginationData,
  tableWidth,
  pagination,
  loading,
}) {
  // const orignalData = data.count ? data.results : data;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <>
      <div
        className={` ${tableWidth} relative bg-white   overflow-auto rounded-lg border border-[#CBD5E1] `}
      >
        <table
          {...getTableProps()}
          loading={loading}
          className={`w-full  text-sm text-left text-gray-500`}
        >
          <thead className=" sticky text-sm top-0 bg-white border-[#CBD5E1] border-b  text-[#181818] ">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    scope="col"
                    style={{ width: column?.width }}
                    className={` px-6 py-4  text-center `}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <p
                      className="flex items-center  justify-center text-[#181818]  text-center m-auto "
                      style={{
                        width: column?.width,
                        fontFamily: "gilRoy-semibold",
                      }}
                    >
                      {column.render("Header")}
                    </p>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="bg-white divide-y divide-gray-200"
          >
            {loading ? (
              <div className="flex-row-reverse absolute top-[50%] left-[50%]">
                <Spinner />
              </div>
            ) : data?.length === 0 ? (
              // Display a row with an image when data length is 0
              <tr>
                <td colSpan={columns.length}>
                  <img
                    style={{
                      width: "350px",
                      position: "absolute",
                      left: "38%",
                      top: "15%",
                    }}
                    src={image}
                    alt="No Data"
                  />
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="bg-white ">
                    {row.cells.map((cell) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          style={{ width: cell.column.width }}
                          className="px-6 py-3 text-center truncate border-b border-[#CBD5E1]"
                          role="cell"
                        >
                          {cell.column.Cell.name === "defaultRenderer" ? (
                            <div
                              style={{ width: cell.column.width }}
                              className="text-sm text-[#6C757D] truncate text-center m-auto "
                            >
                              {cell.render("Cell")}
                            </div>
                          ) : (
                            cell.render("Cell")
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination ? (
        <>
          <div className="sticky bottom-0 w-full   bg-[#f9fafb]  flex  justify-start items-center ">
            <div>
              <div className=" sticky bottom-0 w-full   bg-[#f9fafb]  flex  justify-center">
                <div className="flex  items-baseline">
                  <div className=" flex items-center  text-xs text-[#6C757D]">
                    Showing &nbsp;{" "}
                    <div>
                      <select
                        className=" block w-full m-0 text-xs rounded-sm border border-[#6C757D]  focus:border-[#6C757D] focus:ring focus:ring-white "
                        value={paginationData?.pageSize}
                        onChange={(e) => {
                          // setPageSize(Number(e.target.value));
                          const page_size = Number(e.target.value);
                          setPaginationData({
                            ...paginationData,
                            pageSize: page_size,
                          });
                        }}
                      >
                        {[5, 10, 20].map((pageSize) => (
                          <option
                            key={pageSize}
                            value={pageSize}
                            className=" rounded-sm"
                          >
                            {pageSize}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* <div className="font-medium">{state.pageIndex + 1}</div>{" "} */}
                    &nbsp; of &nbsp;
                    <div className=" tex-xs font-medium">
                      {paginationData?.count} result{" "}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className=" sticky left-[44%]">
              <nav aria-label="Page navigation example">
                <ul className="inline-flex -space-x-px text-sm  ">
                  <li className="p-2 ">
                    <PageButton
                      onClick={() => {
                        const page_Count = paginationData?.pageNumber - 1;
                        gotoPage(paginationData?.page_Count);
                        setPaginationData({
                          ...paginationData,
                          pageNumber: page_Count,
                        });
                      }}
                      disabled={!paginationData?.previous}
                      className="flex items-center justify-center   ml-0 leading-tight text-xs text-gray-500 bg-white   hover:bg-gray-100 hover:text-gray-700 cursor-pointer "
                    >
                      <ChevronLeftIcon className="h-4" /> Prev
                    </PageButton>
                  </li>
                  {/* <li className="p-2 ">
                    <PageButton className="flex items-center justify-center p-2 h-3 w-3  leading-tight text-xs text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 ">
                      1
                    </PageButton>
                  </li>
                  <li className="p-2 ">
                    <PageButton className="flex items-center justify-center p-2 h-3 w-3 text-xs   leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700 ">
                      2
                    </PageButton>
                  </li>
                  <li className="p-2 ">
                    <PageButton
                      
                      className="flex items-center justify-center p-2 h-3 w-3 text-xs bg-blue-600 text-white border border-gray-300 hover:bg-blue-100 hover:text-blue-700 "
                    >
                      3
                    </PageButton>
                  </li> */}

                  <li className="p-2 ">
                    <PageButton
                      onClick={() => {
                        const page_Count = paginationData?.pageNumber + 1;
                        nextPage(page_Count);
                        setPaginationData({
                          ...paginationData,
                          pageNumber: page_Count,
                        });
                      }}
                      disabled={!paginationData?.next}
                      className="flex items-center justify-center text-xs  leading-tight text-gray-500 bg-white   hover:bg-gray-100 hover:text-gray-700  cursor-pointer"
                    >
                      Next <ChevronRightIcon className="h-4" />
                    </PageButton>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </>
      ) : (
        ""
      )}
    </>
  );
}

export default JobTableScreen;
